import { useEffect, type RefObject } from 'react';

/** CSS selector that matches everything that is conventionally focusable
 *  via Tab. `tabindex="-1"` is deliberately excluded — elements with a
 *  negative tabindex are programmatically focusable but not Tab-stop. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'details > summary:first-of-type',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    /* A hidden element has offsetParent === null (unless it's fixed-positioned).
     * Fixed elements always return offsetParent=null so fall through and accept. */
    const style = el.ownerDocument.defaultView?.getComputedStyle(el);
    if (style?.visibility === 'hidden') return false;
    if (style?.display === 'none') return false;
    return true;
  });
}

export interface UseFocusTrapOptions {
  /** When false, the trap is inert. Default: true. */
  enabled?: boolean;
  /** Focus this node on activation. Defaults to the first focusable
   *  descendant, or the container itself if it has `tabindex`. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** When true, does not attempt to focus any element on activation —
   *  lets the caller manage initial focus. Default: false. */
  skipInitialFocus?: boolean;
}

/** Confines Tab / Shift+Tab focus movement within a container. Does NOT
 *  restore focus on deactivation — pair with `useRestoreFocus` for that.
 *
 *  Strategy: on each Tab keydown, compute the live list of focusables.
 *  If Shift+Tab crosses the start, wrap to the end (and vice versa).
 *  This handles dynamic content (e.g., buttons added after render)
 *  correctly without caching a stale list.
 *
 *  Container must have `tabindex="-1"` for programmatic focus to work
 *  when the focusable list is empty (e.g., an empty dialog). */
export function useFocusTrap<T extends HTMLElement>(
  ref: RefObject<T | null>,
  options: UseFocusTrapOptions = {},
): void {
  const { enabled = true, initialFocus, skipInitialFocus = false } = options;

  useEffect(() => {
    if (!enabled) return undefined;
    const container = ref.current;
    if (container === null) return undefined;

    if (!skipInitialFocus) {
      const target = initialFocus?.current ?? getFocusable(container)[0] ?? container;
      try {
        target.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    }

    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        e.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const active = container.ownerDocument.activeElement as HTMLElement | null;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else {
        if (active === last || !container.contains(active)) {
          e.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    };

    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
    };
  }, [ref, enabled, initialFocus, skipInitialFocus]);
}
